import React, { useContext } from 'react';
import { ModalContext, IModalContext, LOGIN_REGISTRATION_MODAL, TERMS_OF_USE_MODAL } from 'contexts/modal';
import { UserContext, IUserContext } from 'contexts/user';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import styles from './style.module.scss';
import { listingPageModalIds, blurIds } from 'constants/test-constants';
import { useTranslation } from 'next-i18next';
import { LOCALE_MODAL_PATHS, LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { buildClassName } from 'utils/build-class-name';
import { useThemeContext } from 'contexts';

import type Listing from 'data/listing';
import { isExpTenant } from '@tenants';

interface Props {
  listing: Listing;
  isVisible: boolean;
}

// This component is for mobile only
export default function ListingPagePrice({ listing, isVisible }: Props) {
  const { statusLabel, isTreb, soldPrice, isSoldWithoutSoldPrice, price, isPrivate: listingIsPrivate, isCrea, providerId, isSignInAndTermsOfUseRequired } = listing;
  const { isAuthenticated } = useContext(UserContext) as IUserContext;
  const { formatCurrency } = useI18nFormatting();
  const isPrivate = !isAuthenticated && listingIsPrivate;
  const isInactive = statusLabel === 'Inactive';
  const { openModal } = useContext(ModalContext) as IModalContext;
  const { t } = useTranslation(Namespaces.COMMON);
  const { tenantId } = useThemeContext();
  const isExpTheme = isExpTenant(tenantId);

  const openSignInModal = () => {
    if (isPrivate || (isSignInAndTermsOfUseRequired && !isVisible && !isAuthenticated)) {
      openModal(LOGIN_REGISTRATION_MODAL, { warningMessage: t(LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_FORM_SIGNIN_REQUIRED_WARNING) });
    } else if (!isVisible) {
      openModal(TERMS_OF_USE_MODAL, { providerId });
    }
  };

  const renderLabel = () => {
    if (isPrivate || (isSignInAndTermsOfUseRequired && !isVisible && !isAuthenticated)) {
      return (
        <section className={styles.cta} onClick={openSignInModal}>
          <span data-testid={blurIds.overlaySignInBtn} className={isExpTheme && styles['sign-in-exp']}>
            {t(LOCALE_COMMON_PATHS.COMPLIANCE_SIGN_IN, { defaultValue: 'Sign in' })}
          </span>{' '}
          {t(LOCALE_COMMON_PATHS.COMPLIANCE_TO_SEE_PRICE, { defaultValue: 'to see price' })}
        </section>
      );
    } else if ((isCrea && !isVisible) || (isSignInAndTermsOfUseRequired && !isVisible)) {
      return (
        <section className={styles.cta} onClick={openSignInModal}>
          <span data-testid={blurIds.overlayAcceptTermsBtn} className={isExpTheme && styles['sign-in-exp']}>
            {t(LOCALE_COMMON_PATHS.COMPLIANCE_ACCEPT_TERMS_OF_USE, { defaultValue: 'Accept Terms of Use' })}
          </span>{' '}
          {t(LOCALE_COMMON_PATHS.COMPLIANCE_TO_SEE_PRICE, { defaultValue: 'to see price' })}
        </section>
      );
    }
  };

  return isInactive && !isTreb ? null :
    <div>
      {soldPrice && (
        <div>
          <div className={!isVisible ? `blur ${styles.price}` : styles.price} data-testid={blurIds.blurredListingPrice}>
            {formatCurrency(soldPrice)}
          </div>
          {isVisible && price &&
            <div className={styles['list-price']}>
              <span data-testid={listingPageModalIds.modalPriceInfoSection}>{formatCurrency(price)}</span>
            </div>
          }
        </div>
      )}
      {!soldPrice && price && (
        <>
          <div className={buildClassName(!isVisible ? `blur ${styles.price}` : styles.price, isSoldWithoutSoldPrice && styles['list-price-only'])} data-testid={blurIds.blurredListingPrice}>
            {isSoldWithoutSoldPrice && 'List: '}{formatCurrency(price)}
          </div>
          {renderLabel()}
        </>
      )}
    </div>;
}
